import type { ApiClient } from '../store/backend';
import {
  updateItemAction,
  deleteItemAction,
  loadedMeetingOccurrences,
  loadedEventOccurrences,
  updateItem,
  fetchEntity,
  fetchOrganizationMeetingOccurrences,
  fetchOrganizationEventOccurrences,
} from '../store/actions/actions';

interface AblyMessage {
  path: string;
  eventType: string;
  item?: unknown;
  hidden?: boolean;
}

interface QueuedNotification {
  message: AblyMessage;
  timestamp: number;
}

interface BatchMetrics {
  firstNotificationTime: number;
  count: number;
}

type EntityType = 'meetingOccurrences' | 'eventOccurrences';

export class AblyBatchNotificationHandler {
  private queues: Map<string, QueuedNotification[]> = new Map();
  private timers: Map<string, number> = new Map();
  private batchMetrics: Map<string, BatchMetrics> = new Map();

  constructor(
    private readonly dispatch: any,
    private readonly api: ApiClient,
    private readonly organizationId: number,
  ) {}

  /**
   * Main entry point - called for each Ably notification
   */
  messageReceived(message: AblyMessage): void {
    const entityType = this.extractEntityType(message.path);

    // If not a batchable entity type, fall back to default behavior
    if (!entityType) {
      this.dispatch(updateItem(this.api, message) as any);
      return;
    }

    const queueKey = `${entityType}-${this.organizationId}`;

    // Phase 1: Dispatch inline update IMMEDIATELY (optimistic UI)
    if (message.eventType === 'delete') {
      this.dispatch(deleteItemAction(message));
    } else if (message.item || message.hidden) {
      this.dispatch(updateItemAction(message));
    }

    // Phase 2: Queue for later fetch (to get fresh authoritative data)
    this.queueNotification(queueKey, entityType, message);
  }

  /**
   * Extract entity type from path if it's a batchable entity
   */
  private extractEntityType(path: string): EntityType | null {
    if (path.includes('/meetingOccurrences/')) {
      return 'meetingOccurrences';
    } else if (path.includes('/eventOccurrences/')) {
      return 'eventOccurrences';
    }
    return null;
  }

  /**
   * Queue notification and schedule processing
   */
  private queueNotification(queueKey: string, entityType: EntityType, message: AblyMessage): void {
    const now = Date.now();

    // Initialize queue if needed
    if (!this.queues.has(queueKey)) {
      this.queues.set(queueKey, []);
      this.batchMetrics.set(queueKey, {
        firstNotificationTime: now,
        count: 0,
      });
      console.log(`[BatchNotificationHandler] Started tracking batch for ${entityType} at ${now}`);
    }

    // Add to queue
    const queue = this.queues.get(queueKey)!;
    queue.push({ message, timestamp: now });

    // Update metrics
    const metrics = this.batchMetrics.get(queueKey)!;
    metrics.count++;

    console.log(`[BatchNotificationHandler] Queued ${entityType} notification (${metrics.count} in batch)`);

    // Schedule processing with adaptive delay
    this.scheduleProcessing(queueKey, entityType);
  }

  /**
   * Schedule processing with adaptive timing based on queue size
   */
  private scheduleProcessing(queueKey: string, entityType: EntityType): void {
    const queue = this.queues.get(queueKey)!;
    const metrics = this.batchMetrics.get(queueKey)!;
    const now = Date.now();
    const timeSinceFirst = now - metrics.firstNotificationTime;

    // Clear existing timer
    const existingTimer = this.timers.get(queueKey);
    if (existingTimer !== undefined) {
      window.clearTimeout(existingTimer);
    }

    // Adaptive delay based on queue size
    let delay: number;
    if (queue.length === 1) {
      delay = 150; // Fast, but catches rapid sequences
    } else if (queue.length === 2) {
      delay = 400; // Wait to see if more arrive
    } else {
      delay = 300; // Bulk detected, shorter delay
    }

    // Force processing if we've been waiting too long (max 1500ms from first notification)
    const maxWait = 1500;
    if (timeSinceFirst + delay > maxWait) {
      delay = Math.max(0, maxWait - timeSinceFirst);
    }

    // Schedule processing
    const timer = window.setTimeout(() => {
      this.processQueue(queueKey, entityType);
    }, delay);

    this.timers.set(queueKey, timer);
  }

  /**
   * Process batched notifications
   */
  private async processQueue(queueKey: string, entityType: EntityType): Promise<void> {
    const queue = this.queues.get(queueKey);
    const metrics = this.batchMetrics.get(queueKey);

    if (!queue || !metrics || queue.length === 0) {
      return;
    }

    const now = Date.now();
    const totalTime = now - metrics.firstNotificationTime;

    console.log(`[${entityType} Batch] Processing ${queue.length} notifications received over ${totalTime}ms`);

    const startTime = Date.now();

    try {
      // Decide strategy
      if (this.shouldUseBatchFetch(queue)) {
        await this.processBatchFetch(entityType, queue);
      } else {
        await this.processIndividualFetches(queue);
      }

      const processingTime = Date.now() - startTime;
      const avgInterval = queue.length > 1 ? totalTime / queue.length : 0;

      console.log(`[${entityType} Batch] Complete: ${processingTime}ms processing, ${totalTime}ms total`, {
        strategy: this.shouldUseBatchFetch(queue) ? 'batch-fetch' : 'individual-fetch',
        count: queue.length,
        avgNotificationInterval: avgInterval.toFixed(1) + 'ms',
      });
    } catch (error) {
      console.error(`[${entityType} Batch] Error processing batch:`, error);
    } finally {
      // Clean up
      this.queues.delete(queueKey);
      this.timers.delete(queueKey);
      this.batchMetrics.delete(queueKey);
    }
  }

  /**
   * Decide whether to use batch fetch or individual fetches
   */
  private shouldUseBatchFetch(queue: QueuedNotification[]): boolean {
    return queue.length > 2;
  }

  /**
   * Process using batch fetch (fetch entire list)
   */
  private async processBatchFetch(entityType: EntityType, queue: QueuedNotification[]): Promise<void> {
    console.log(`[${entityType} Batch] Strategy: batch-fetch`);
    console.log(`[${entityType} Batch] Fetching entire list (optimized for ${queue.length} updates)`);

    try {
      if (entityType === 'meetingOccurrences') {
        const occurrences = await fetchOrganizationMeetingOccurrences(this.api, this.organizationId);
        this.dispatch(loadedMeetingOccurrences(occurrences));
        console.log(`[${entityType} Batch] Refreshed ${occurrences.length} items from list fetch`);
      } else if (entityType === 'eventOccurrences') {
        const occurrences = await fetchOrganizationEventOccurrences(this.api, this.organizationId);
        this.dispatch(loadedEventOccurrences(occurrences));
        console.log(`[${entityType} Batch] Refreshed ${occurrences.length} items from list fetch`);
      }
    } catch (error) {
      console.error(`[${entityType} Batch] Error fetching list:`, error);
      throw error;
    }
  }

  /**
   * Process using individual fetches (1-2 items)
   */
  private async processIndividualFetches(queue: QueuedNotification[]): Promise<void> {
    console.log(`[Batch] Strategy: individual-fetch (${queue.length} items)`);

    for (const { message } of queue) {
      try {
        // Skip deletes - already dispatched in messageReceived
        if (message.eventType === 'delete') {
          continue;
        }

        // Fetch fresh data from API
        const entity = await fetchEntity(this.api, message.path);
        if (entity) {
          const updatedMessage = { ...message, item: entity };
          this.dispatch(updateItemAction(updatedMessage));
        }
      } catch (error) {
        console.error(`[Batch] Error fetching individual item ${message.path}:`, error);
      }
    }
  }
}
